import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * "The AI Corridor" e-book's own page.
 *
 * Written to the brief sent on 2026-09-25, and built band for band like the
 * sibling e-book pages (Top 10 Secrets, Generations of Excellence, Agentic AI):
 * two-column hero with the cover artwork beside it, a grey "Why Download?" card,
 * a "What You'll Learn" card with the bullets, a centred closing ask, and the
 * Hall of Fame card at the foot. Asked for in those words — *"prepare the page
 * as in harmony with other ebook pages uyumlu ve tutarlı bir şekilde olsun"*.
 *
 * **The Download buttons scroll to the form rather than opening a file**, which
 * is what *"download cta leri aşağı kaydırsın öbür ebooklardaki sistemin aynısı
 * olsun"* asks for. On the sibling pages that happens by itself: a CTA whose
 * `href` is a PDF is rewritten to `#contact` by `lib/gatedDownload.ts`, the
 * reader is asked for an email, and the file rides along so the thank-you panel
 * can hand it over. The PDF arrived on 2026-09-25 and `DOWNLOAD` is now its
 * bucket URL, so this page works exactly like its siblings.
 *
 * The lead the form opens is already tagged by page: `ContactForm` posts
 * `source_page` and `source_title`, so a submission here reaches the CRM as
 * this address and this title, the same way every other e-book page's does.
 * There is no per-page tag to set.
 *
 * The five images are the ones attached with the brief. The cover artwork is the
 * hero (square, like the sibling covers — the site's 3:2 hero box would take the
 * corridor's ceiling and floor off); the isometric bridge carries "Why
 * Download?", because the climb up into a walled, lit walkway is the argument;
 * the isometric platforms carry "What You'll Learn", one platform per trust
 * capability; the receding frames punctuate the nine postures; and the printed
 * mockup sits beside the closing ask, which is where the sibling pages put it.
 */
export const route: RouteDef = {
  id: 'ebookTheAiCorridor',
  path: 'impact-labs/ebooks/the-ai-corridor',
  priority: 0.7,
}

/**
 * Where the Download buttons go: the e-book's PDF in the `website-documents`
 * bucket, uploaded on 2026-09-25. The buttons still scroll to the form rather
 * than opening the file — `lib/gatedDownload.ts` rewrites a PDF href to
 * `#contact` and carries the file alongside it, so the reader is asked for an
 * email and the thank-you panel hands the download over.
 *
 * A local constant rather than a key in `content/pages/links.ts`: a page's own
 * download is nobody else's, and that file is shared.
 */
export const DOWNLOAD
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '67d1195a-452e-46d2-a447-5ebfecf25cf0-The-AI-Corridor-Digitopia.pdf'

export const ebookTheAiCorridor: WrittenPage = {
  navLabel: 'The AI Corridor',
  title: 'The AI Corridor: Finding Your AI Posture',
  description:
    'Innovation or governance? Neither alone. Find your AI posture on the corridor where appetite and earned trust rise together. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Digitopia Insight',
      headline: 'The AI Corridor',
      sub: 'How to find your AI posture between innovation and trust.',
      body: [
        'Every board hears two voices: move faster, and slow down. Most try to balance them. '
          + 'This ebook argues that balance is the mistake, and shows how to find the corridor '
          + 'where your appetite for AI and your earned trust in it rise together.',
      ],
      ctas: [{ label: 'Download', href: DOWNLOAD }],
      photo: {
        src: '/photo/the-ai-corridor-ebook-artwork.png',
        alt:
          'The e-book’s cover: a figure walking away down a long glass corridor lit by blue and '
          + 'violet light, towards daylight at the far end.',
      },
      // The cover is square, and the site's 3:2 hero box would take the
      // corridor's ceiling and floor off — which is the picture.
      photoFrame: 'square',
      check:
        'The PDF is now wired to all four Download buttons, so the gate, the '
        + 'email and the thank-you panel work as they do on the other e-book '
        + 'pages. Two things are still open: this e-book has no '
        + '`content/ebooks/` catalogue entry, so it does not appear on the e-books '
        + 'index, and the cover artwork is 1240px rather than the site’s usual '
        + '2560 — a larger export, if one exists, drops in under the same name.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Artwork left, offer right — it alternates with the card under it.
      flip: true,
      heading: 'Why Download?',
      body: [
        'Nearly every company now uses AI, yet few see real returns, and shadow AI incidents are '
          + 'rising fast. The problem is not too much innovation or too much governance. It is '
          + 'treating them as a trade-off.',
        'This ebook gives leadership teams a shared picture of where they stand, which way to '
          + 'move, and how governance becomes the lever that lets AI go faster, not the brake '
          + 'that holds it back.',
      ],
      ctas: [{ label: 'Download the Ebook', href: DOWNLOAD }],
      photo: {
        src: '/photo/the-ai-corridor-ebook-corridor.png',
        alt:
          'An illustration of a raised walkway: people climbing the steps at one end, desks and '
          + 'screens along the deck, and a glazed control room at the top.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      sub:
        'A practical framework for boards and leadership teams deciding how far and how fast to '
        + 'go with AI. This guide includes:',
      points: [
        'The Posture Chart: AI Innovation Appetite against Trust and Confidence in AI',
        'Four archetypes and nine postures, from the Bystander to the Self-Driving Enterprise',
        'The two ways of falling out of the corridor: the Overreach Zone and the Complacency Zone',
        'The six trust capabilities that turn naive trust into earned trust',
        'Seven moves to get into the corridor and stay there',
        'A 10-question Posture Self-Assessment to score your own organisation',
      ],
      ctas: [{ label: 'Download the Ebook', href: DOWNLOAD }],
      photo: {
        src: '/photo/the-ai-corridor-ebook-capabilities.png',
        alt:
          'An illustration of four linked platforms — a desk, a sealed model enclosure, a row of '
          + 'servers and a control table — with people working at each.',
      },
    },
    {
      // The nine postures, in a picture: the same frame at nine depths, one
      // current running through all of them.
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Nine postures, one corridor.',
      body: [
        'A posture is not a score to be proud of or ashamed of. It is a position on a chart, and '
          + 'every position has a next move. The book names all nine, says which of them are '
          + 'inside the corridor, and tells you what the move out of yours looks like.',
      ],
      ctas: [{ label: 'Download the Ebook', href: DOWNLOAD }],
      photo: {
        src: '/photo/the-ai-corridor-ebook-postures.png',
        alt:
          'An illustration of six rounded frames receding into the distance, with a single bright '
          + 'current threading through all of them.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Stop Looking for Balance. Start Walking the Diagonal.',
      body: ['Download the free Ebook and find your AI posture today.'],
      ctas: [{ label: 'Download the Ebook', href: DOWNLOAD }],
      photo: {
        src: '/photo/the-ai-corridor-ebook-cover.png',
        alt: 'A printed copy of The AI Corridor standing on a white background.',
      },
    },
  ],
}
